import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  retryWhen,
  scan,
  switchMap,
  take,
  throwError,
  timer
} from 'rxjs';
import { AuthService } from '../../modules/common/modules/auth/services/auth.service';
import { IAuth } from '../../modules/common/modules/auth/models/auth.model';

/**
 * Improved HTTP interceptor that handles:
 * 1. Automatic Bearer token attachment
 * 2. Network error retries with exponential backoff
 * 3. Token refresh on 401 errors with concurrency protection
 * 4. Automatic logout on refresh failure
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  // Network retry configuration
  private readonly maxRetries = 4;
  private readonly backoffBase = 500;

  // Token refresh concurrency control
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService
  ) {}

  intercept(
    originalReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip if not authenticated or OPTIONS request
    if (!this.authService.isAuthenticated() || originalReq.method === 'OPTIONS') {
      return next.handle(originalReq);
    }

    // Only attach auth to API requests
    if (!originalReq.url.includes('/api')) {
      return next.handle(originalReq);
    }

    // Proactively refresh shortly before expiration to reduce 401 churn.
    const authedRequest$ = this.authService.ensureFreshToken(30).pipe(
      map((token) => this.addAuthToken(originalReq, token)),
      catchError(() => {
        try {
          const fallbackToken = this.authService.getAccessToken();
          return of(this.addAuthToken(originalReq, fallbackToken));
        } catch {
          // Continue without auth header so backend can return 401 and
          // interceptor can drive the re-auth flow.
          return of(originalReq);
        }
      }),
      switchMap((requestWithToken) => next.handle(requestWithToken))
    );

    return authedRequest$.pipe(
      // Retry network failures with exponential backoff
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, err: HttpErrorResponse) => {
            // Only retry network errors (status 0)
            if (err.status === 0 && retryCount < this.maxRetries) {
              return retryCount + 1;
            }
            // Rethrow if not a network error or retries exhausted
            throw err;
          }, 0),
          // Exponential backoff: backoffBase * 2^(retryCount-1)
          mergeMap(retryCount =>
            timer(this.backoffBase * Math.pow(2, retryCount - 1))
          )
        )
      ),

      // Handle authentication errors
      catchError((error: HttpErrorResponse) => {
        // Only handle 401 here. Network errors are handled by retryWhen above.
        if (error.status === 401) {
          return this.handle401Error(originalReq, next);
        }

        // Pass through other errors
        return throwError(() => error);
      })
    );
  }

  /**
   * Handles 401 errors by refreshing the token
   * Prevents concurrent refresh attempts using BehaviorSubject
   */
  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // If a refresh is already in progress, wait for it
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          return next.handle(this.addAuthToken(req, token!));
        })
      );
    }

    // Start a new refresh
    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken('token_refresh_interceptor').pipe(
      switchMap((auth: IAuth) => {
        // Refresh successful
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next(auth.access_token);

        // Retry the original request with new token
        return next.handle(this.addAuthToken(req, auth.access_token));
      }),
      catchError(refreshError => {
        // Refresh failed: re-initiate login from the current route so UX does
        // not pass through an intermediate blank /auth/login page.
        this.refreshTokenInProgress = false;
        const returnUrl = window.location.pathname + window.location.search + window.location.hash;
        this.authService.clearSession();
        this.authService.login(returnUrl).catch(() => {
          window.location.href = `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
        });

        return throwError(() => refreshError);
      })
    );
  }

  /**
   * Adds authorization header to request
   * Only adds to API requests to avoid exposing token to external domains
   */
  private addAuthToken(
    req: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    // Only add auth header for API requests
    if (!req.url.includes('/api')) {
      return req;
    }

    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`)
    });
  }
}
